import { checkboxAnatomy } from "@chakra-ui/anatomy"
import {
  StyleFunctionProps,
  createMultiStyleConfigHelpers,
  defineStyleConfig,
  extendTheme,
  withDefaultVariant,
} from "@chakra-ui/react"
import { chain } from "lodash"

import theme from "./theme.config"

const Input = createMultiStyleConfigHelpers([
  "field",
  "addon",
  "element",
]).defineMultiStyleConfig({
  variants: {
    outline: (props: StyleFunctionProps) => ({
      field: {
        bg: props.colorMode === "dark" ? theme["gray.700"] : theme.white,
        borderColor:
          props.colorMode === "dark" ? theme["gray.600"] : theme["gray.200"],
        color: "primary",
      },
    }),
  },
})

const Checkbox = createMultiStyleConfigHelpers(
  checkboxAnatomy.keys
).defineMultiStyleConfig({
  variants: {
    darkerborder: (props: StyleFunctionProps) => ({
      control: {
        borderColor:
          props.colorMode === "dark" ? theme["gray.600"] : theme["gray.400"],
      },
    }),
  },
})

const Button = defineStyleConfig({
  sizes: {
    xs: {
      lineHeight: 0.75,
    },
  },
  variants: {
    nav: (props: StyleFunctionProps) => ({
      borderRadius: "md",
      bg: props.colorMode === "dark" ? theme["gray.900"] : theme.white,
      borderColor:
        props.colorMode === "dark" ? theme["gray.600"] : theme["gray.200"],
      borderWidth: "1px",
    }),
  },
})

const Select = createMultiStyleConfigHelpers([
  "field",
  "icon",
]).defineMultiStyleConfig({
  baseStyle: {},
  variants: {
    nav: (props: StyleFunctionProps) => ({
      field: {
        borderRadius: "md",
        bg: props.colorMode === "dark" ? theme["gray.900"] : theme.white,
        borderColor:
          props.colorMode === "dark" ? theme["gray.600"] : theme["gray.200"],
        borderWidth: "1px",
      },
    }),
    outline: (props: StyleFunctionProps) => ({
      field: {
        borderRadius: "md",
        bg: props.colorMode === "dark" ? theme["gray.700"] : theme.white,
        borderColor:
          props.colorMode === "dark" ? theme["gray.600"] : theme["gray.200"],
        color: "primary",
      },
    }),
  },
})

const Menu = createMultiStyleConfigHelpers(["item"]).defineMultiStyleConfig({
  baseStyle: (props) => ({
    item: {
      color: props.colorMode === "dark" ? theme.white : theme["gray.100"],
    },
  }),
})

const Table = createMultiStyleConfigHelpers([
  "table",
  "td",
  "th",
  "caption",
]).defineMultiStyleConfig({
  baseStyle: {},
  variants: {
    details: (props: StyleFunctionProps) => ({
      table: {},
      th: {
        padding: "2px 12px 2px 2px",
        // fontSize: "18px",
        fontWeight: 500,
        // lineHeight: "28px",
        textTransform: "none",
        color: "secondary",
        verticalAlign: "top",
        letterSpacing: "none",
        whiteSpace: "nowrap",
      },
      td: {
        width: "100%",
        padding: "2px",
        // fontSize: "18px",
        fontWeight: 500,
        // lineHeight: "28px",
        color: "primary",
        verticalAlign: "top",
        letterSpacing: "none",
      },
    }),
  },
})

const Heading = defineStyleConfig({
  sizes: {
    md: {
      fontSize: "18px",
      fontWeight: 600,
    },
  },
})

const Modal = createMultiStyleConfigHelpers([
  "header",
  "overlay",
  "dialogContainer",
  "dialog",
  "closeButton",
  "body",
  "footer",
]).defineMultiStyleConfig({
  baseStyle: {},
  variants: {
    warning: (props: StyleFunctionProps) => ({
      header: {
        color: "primary",
      },
      // overlay: {
      //   backdropFilter: "blur(10px)",
      // },
      dialogContainer: {},
      dialog: {
        border: "2px solid",
        borderColor: "red.600",
      },
      closeButton: {},
      body: { color: "primary" },
      // footer: {
      //   justifyContent: "space-between",
      // },
    }),
  },
})

export const chakraTheme = extendTheme(
  withDefaultVariant({
    variant: "outline",
    components: ["Button", "Input", "Select"],
  }),
  {
    fonts: {
      heading: "'Inter', sans-serif",
      body: "'Inter', sans-serif",
    },
    semanticTokens: {
      colors: {
        primary: {
          default: theme.primary,
          _dark: theme.darkPrimary,
        },
        secondary: {
          default: theme.secondary,
          _dark: theme.darkSecondary,
        },
        tertiary: {
          default: theme.tertiary,
          _dark: theme.darkTertiary,
        },
        quaternary: {
          default: theme.brick,
          _dark: theme.maroon,
        },

        // Include all "color.value" colours (e.g. `{gray: {100: "#FFFFFF"}}`)
        ...chain(theme)
          .entries()
          .filter((v) => v.includes("."))
          .mapKeys(([key]) => key)
          .groupBy(([key]) => key.split(".")[0])
          .mapValues(([value]) =>
            chain(value)
              .mapKeys(([key]) => key.split(".")[1])
              .mapValues(([_, color]) => color)
          )
          .value(),
      },
    },
    components: {
      Button,
      Select,
      Input,
      Checkbox,
      Table,
      Heading,
      Menu,
      Modal,
    },
  }
)
